import Rollbar from 'rollbar';

// Angular imports
import { AngularFireAuthGuardModule } from '@angular/fire/compat/auth-guard';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Injectable, InjectionToken, NgModule, ErrorHandler, APP_INITIALIZER, inject } from '@angular/core';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { ReactiveFormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import {
  EmailLinkAuthFormComponent,
  provideFirebaseUI,
} from '@firebase-oss/ui-angular';
import { initializeUI, providerPopupStrategy, providerRedirectStrategy } from '@firebase-oss/ui-core';

// App imports (mostly components to declare)
import { AnalyzeComponent } from './views/analyze/analyze.component';
import { AnalyzeDialogComponent } from './views/analyzedialog/analyzedialog.component';
import { AppComponent } from './app.component';
import { ApplicantComponent } from './views/applicant/applicant.component';
import { ApplyComponent } from './views/apply/apply.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthService } from './services/auth.service';
import { CanDeactivateGuard } from './can-deactivate.guard';
import { ConfirmListDialogComponent } from './views/confirmlistdialog/confirmlistdialog.component';
import { DenyDialogComponent } from './views/denydialog/denydialog.component';
import { EditApplicantDialogComponent } from './views/editapplicantdialog/editapplicantdialog.component';
import { environment } from 'src/environments/environment';
import { FaqComponent } from './views/faq/faq.component';
import { HeaderComponent } from './views/header/header.component';
import { provideAngularFireCompatInjectionContextFix } from './firebase-compat-injection-context';
import { HistoryComponent } from './views/history/history.component';
import { HomeComponent } from './views/home/home.component';
import { HorrorLevelComponent } from './views/horrorlevel/horrorlevel.component';
import { HorrorLevelPickerComponent } from './views/horrorlevelpicker/horrorlevelpicker.component';
import { HorrorPreferenceComponent } from './views/horrorpreference/horrorpreference.component';
import { HorrorPreferencePickerComponent } from './views/horrorpreferencepicker/horrorpreferencepicker.component';
import { LanguageComponent } from './views/language/language.component';
import { LanguagePickerComponent } from './views/languagepicker/languagepicker.component';
import { LocationPickerComponent } from './views/locationpicker/locationpicker.component';
import { LoginComponent } from './views/login/login.component';
import { MaterialModule } from './material.module';
import { NominateComponent } from './views/nominate/nominate.component';
import { NominationComponent } from './views/nomination/nomination.component';
import { ProfileComponent } from './views/profile/profile.component';
import { QuoteComponent } from './views/quote/quote.component';
import { RankComponent } from './views/rank/rank.component';
import { ContactComponent } from './views/contact/contact.component';
import { RequestResubmitDialogComponent } from './views/requestresubmitdialog/requestresubmitdialog.component';
import { ReviewApplicantsComponent } from './views/reviewapplicants/reviewapplicants.component';
import { ReviewPublicDataComponent } from './views/reviewpublicdata/reviewpublicdata.component';
import { ReviewRoomsComponent } from './views/reviewrooms/reviewrooms.component';
import { RoomComponent } from './views/room/room.component';
import { RoomDisclosureComponent } from './views/roomdisclosure/roomdisclosure.component';
import { RoomEditorComponent } from './views/roomeditor/roomeditor.component';
import { RoomFilter, UserFilter } from './utils/misc.utils';
import { RoomStatusReasonDialogComponent } from './views/roomstatusreasondialog/roomstatusreasondialog.component';
import { SettingsService } from './services/settings.service';
import { SimpleDialogComponent } from './views/simpledialog/simpledialog.component';
import { SimpleRoomComponent } from './views/simpleroom/simpleroom.component';
import { SimpleTextInputDialogComponent } from './views/simpletextinputdialog/simpletextinputdialog.component';
import { SolutionComponent } from './views/solution/solution.component';
import { UnrankRoomDialogComponent } from './views/unrankroomdialog/unrankroomdialog.component';
import { UserDisclosureComponent } from './views/userdisclosure/userdisclosure.component';
import { ValidateComponent } from './views/validate/validate.component';
import { VersionUpdatedDialogComponent } from './views/versionupdateddialog/versionupdateddialog.component';
import { PartnerImportDialogComponent } from './views/partnerimportdialog/partnerimportdialog.component';
import { PartnerPlayedDialogComponent } from './views/partnerplayeddialog/partnerplayeddialog.component';
import { VouchDialogComponent } from './views/vouchdialog/vouchdialog.component';
import { AngularFireFunctionsModule } from '@angular/fire/compat/functions';

export const ROLLBAR_SERVICE = new InjectionToken<Rollbar>('rollbar');

// webkit-masked-url://hidden/ is how Safari labels injected extension/browser scripts (Rollbar #538).
const EXTENSION_URL = /^(?:(?:chrome|moz|safari-web|ms-browser)-extension|webkit-masked-url):\/\//;

// Our code only ever runs from bundled .js files. A frame whose filename is a page on our own origin is a script the browser
// injected inline (e.g. Chrome iOS's translate script, which can recurse until the stack overflows, Rollbar #424).
function isInjectedPageFrame(filename: string): boolean {
  try {
    const url = new URL(filename);
    return url.origin === window.location.origin && !url.pathname.endsWith('.js');
  } catch {
    return false;
  }
}

// checkIgnore's third argument is the payload's `data` object (rollbar.js Queue.addItem passes item.data), so body is top-level.
function isForeignOnlyTrace(data: any): boolean {
  const body = data?.body;
  const traces = body?.trace_chain ?? (body?.trace ? [body.trace] : []);
  const frames = traces.flatMap((trace: any) => trace?.frames ?? []);
  return frames.length > 0 && frames.every((frame: any) => {
    const filename = String(frame?.filename ?? '');
    return EXTENSION_URL.test(filename) || isInjectedPageFrame(filename);
  });
}

// AngularFire lazy-loads Firestore/Functions via import(); in a tab opened before a deploy those chunks are gone and the
// hosting rewrite serves index.html instead. Safari, Chrome and Firefox word the resulting failure differently. Because
// firebase.json labels every *.js response as JavaScript, Chrome and Firefox parse that HTML and fail on its first '<';
// those are matched exactly so a JSON.parse of an HTML response ("Unexpected token '<', \"<!doctype\"...") isn't caught.
const CHUNK_LOAD_ERROR = new RegExp([
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'is not a valid JavaScript MIME type',
  '^Unexpected token \'<\'$',
  '^expected expression, got \'<\'$'
].join('|'), 'i');
const CHUNK_RELOAD_KEY = 'chunkLoadReloadAt';
const CHUNK_RELOAD_MIN_INTERVAL_MS = 60 * 1000;

function isChunkLoadError(data: any): boolean {
  const body = data?.body;
  const traces = body?.trace_chain ?? (body?.trace ? [body.trace] : []);
  const messages = [body?.message?.body, ...traces.map((trace: any) => trace?.exception?.message)];
  return messages.some((message: any) => typeof message === 'string' && CHUNK_LOAD_ERROR.test(message));
}

// After a laptop sleeps with a tab open, the Firestore SDK's first reconnect request fails and its rejection escapes as an
// uncaught, stackless "Failed to fetch"; the SDK recovers on its own. Only drop it when the last recorded network call was
// Firestore's, so a failed fetch from anywhere else (e.g. a Cloud Function call) is still reported.
function isFirestoreReconnectFetchFailure(data: any): boolean {
  const body = data?.body;
  const traces = body?.trace_chain ?? (body?.trace ? [body.trace] : []);
  if (!traces.some((trace: any) => trace?.exception?.message === 'Failed to fetch')) {
    return false;
  }
  const telemetry: any[] = Array.isArray(body?.telemetry) ? body.telemetry : [];
  const lastNetwork = [...telemetry].reverse().find((event: any) => event?.type === 'network');
  return String(lastNetwork?.body?.url ?? '').startsWith('https://firestore.googleapis.com/');
}

// A Firestore listener that gets permission-denied while signed out rejects uncaught (no `code` in the logged arguments), so
// match the FirebaseError by its class and message instead.
function isSignedOutPermissionDenied(data: any): boolean {
  if (AuthService.signedIn) {
    return false;
  }
  const body = data?.body;
  const traces = body?.trace_chain ?? (body?.trace ? [body.trace] : []);
  return traces.some((trace: any) =>
    trace?.exception?.class === 'FirebaseError' && trace?.exception?.message === 'Missing or insufficient permissions.');
}

// The roomfinder host serves a different site, which a restored or cached tab can still pair with this app's old bundle (Rollbar
// #369, #382, #387). Reloading can't fix it there and the errors aren't actionable.
function isRoomFinderHost(): boolean {
  try {
    return window.location.hostname === environment.roomFinderHostname;
  } catch {
    return false;
  }
}

// Crawlers run the app in stripped-down headless browsers and hit errors real users never see (e.g. the BuiltWith crawler's
// "t.auth is not a function" from inside AngularFire's compat setup, Rollbar #526). Bot user agents name themselves as
// "Somebot/1.0"; matching that form (not a bare "bot") avoids catching phone models like the Cubot.
const CRAWLER_USER_AGENT = /bot\/|crawl|spider|BuiltWith|HeadlessChrome/i;

function isCrawler(): boolean {
  try {
    return CRAWLER_USER_AGENT.test(navigator.userAgent);
  } catch {
    return false;
  }
}

// Reloads to pick up the current build, at most once a minute so a chunk that's genuinely broken can't loop the page.
// Returns false (so the error is still reported) when it didn't reload.
function reloadForChunkLoadError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
    if (last && Date.now() - last < CHUNK_RELOAD_MIN_INTERVAL_MS) {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

const rollbarConfig = {
  accessToken: environment.rollbarAccessToken,
  environment: environment.production ? 'production' : 'development',
  captureUncaught: true,
  captureUnhandledRejections: true,
  // Browsers reduce errors from cross-origin or injected scripts to a bare "Script error." with no stack; ours always have details.
  // Safari web extensions throw the "Tab not found" error from their own content scripts, with no stack; the app never
  // calls runtime.sendMessage. Rollbar compiles these entries as regexes.
  ignoredMessages: ['Script error.', 'Invalid call to runtime\\.sendMessage\\(\\)\\. Tab not found\\.'],
  // A throw in here makes Rollbar drop checkIgnore for the rest of the page, so guard every access.
  checkIgnore: (isUncaught: boolean, args: Rollbar.LogArgument[] | undefined, data: any) =>
    isCrawler() ||
    isRoomFinderHost() ||
    isSignedOutPermissionDenied(data) ||
    // Firestore listeners still open when the user signs out get permission-denied before their components are destroyed.
    // args is undefined for errors that only came through window.onerror (Rollbar sets _originalArgs only when present).
    (!AuthService.signedIn && Array.isArray(args) && args.some((arg: any) => arg?.code === 'permission-denied')) ||
    // Errors thrown entirely inside browser extension or browser-injected scripts (e.g. one wrapping window.fetch) aren't ours.
    (isUncaught && isForeignOnlyTrace(data)) ||
    (isUncaught && isFirestoreReconnectFetchFailure(data)) ||
    // A stale tab can't load the current build's chunks; reloading fixes it, so only report if we couldn't reload.
    (isChunkLoadError(data) && reloadForChunkLoadError()),
  verbose: !environment.production,
  recorder: {
    enabled: true
  },
  payload: {
    client: {
      javascript: {
        // eslint-disable-next-line camelcase
        source_map_enabled: true,
        // code_version will be set dynamically if available
        // eslint-disable-next-line camelcase
        code_version: (typeof window !== 'undefined' && (<any>window).ROLLBAR_CODE_VERSION) || undefined
      }
    }
  }
};

@Injectable({ providedIn: 'any' })
export class RollbarErrorHandler implements ErrorHandler {
  private rollbar = inject<Rollbar>(ROLLBAR_SERVICE);


  handleError(err: any): void {
    this.rollbar.error(err.originalError || err);
  }
}

export function rollbarFactory() {
  return new Rollbar(rollbarConfig);
}

export function settingsFactory(settings: SettingsService) {
  return async () => { await settings.loadSettings(); };
}

@NgModule({
  imports: [
    AngularFireAuthGuardModule,
    AngularFireAuthModule,
    AngularFireFunctionsModule,
    AngularFireModule.initializeApp(environment.firebase, environment.firebase.projectId),
    AngularFirestoreModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    DragDropModule,
    EmailLinkAuthFormComponent,
    MaterialModule,
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsMeasurementId),
    NgxGoogleAnalyticsRouterModule,
    ReactiveFormsModule,
    TextFieldModule
  ],
  declarations: [
    AnalyzeComponent,
    AnalyzeDialogComponent,
    AppComponent,
    ApplicantComponent,
    ApplyComponent,
    ConfirmListDialogComponent,
    DenyDialogComponent,
    EditApplicantDialogComponent,
    FaqComponent,
    HeaderComponent,
    HistoryComponent,
    HomeComponent,
    HorrorLevelComponent,
    HorrorLevelPickerComponent,
    HorrorPreferenceComponent,
    HorrorPreferencePickerComponent,
    LanguageComponent,
    LanguagePickerComponent,
    LocationPickerComponent,
    LoginComponent,
    NominateComponent,
    NominationComponent,
    ProfileComponent,
    QuoteComponent,
    RankComponent,
    ContactComponent,
    RequestResubmitDialogComponent,
    ReviewApplicantsComponent,
    ReviewPublicDataComponent,
    ReviewRoomsComponent,
    RoomComponent,
    RoomDisclosureComponent,
    RoomEditorComponent,
    RoomFilter,
    RoomStatusReasonDialogComponent,
    SimpleDialogComponent,
    SimpleRoomComponent,
    SimpleTextInputDialogComponent,
    SolutionComponent,
    UnrankRoomDialogComponent,
    UserDisclosureComponent,
    UserFilter,
    ValidateComponent,
    VersionUpdatedDialogComponent,
    PartnerImportDialogComponent,
    PartnerPlayedDialogComponent,
    VouchDialogComponent,
  ],
  providers: [
    AuthService,
    CanDeactivateGuard,
    provideAngularFireCompatInjectionContextFix(),
    provideFirebaseApp(() => initializeApp(environment.firebase, environment.firebase.projectId)),
    provideAuth(() => getAuth(getApp(environment.firebase.projectId))),
    provideFirebaseUI((apps) => {
      const app = apps.find((candidate) => candidate.name === environment.firebase.projectId) || apps[0];
      const useRedirect = environment.production &&
        typeof window !== 'undefined' &&
        window.location.hostname !== 'localhost';
      return initializeUI({
        app,
        behaviors: [useRedirect ? providerRedirectStrategy() : providerPopupStrategy()],
      });
    }),
    { provide: APP_INITIALIZER, useFactory: settingsFactory, deps: [SettingsService], multi: true },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: ROLLBAR_SERVICE, useFactory: rollbarFactory },
    provideHttpClient(withInterceptorsFromDi())
  ],
  bootstrap: [
    AppComponent,
  ]
})
export class AppModule {
}
